import { useLayoutEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

/**
 * Initializes an inline page form exactly once after its mount refetch finishes.
 * After initialization the form owns its state; later query updates never reset user input.
 */
export function useInitializeFormFromFreshQuery<TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  value: TFieldValues,
  fresh: boolean,
  scopeKey?: unknown,
) {
  const initialized = useRef(fresh);
  const initializedScope = useRef(scopeKey);

  useLayoutEffect(() => {
    if (initializedScope.current !== scopeKey) {
      initializedScope.current = scopeKey;
      initialized.current = false;
    }
    if (initialized.current || !fresh) return;
    form.reset(value);
    initialized.current = true;
  }, [form, fresh, scopeKey, value]);
}
